import axios, { AxiosRequestConfig } from "axios";
import { DependencyList, useCallback, useEffect, useRef, useState } from "react";
import type {
  Announcement,
  AnnouncementRecord,
  AuthUser,
  EventPage,
  EventSeverity,
  EventType,
  Health,
  Page,
  Product,
  ProductDetail,
  ProductStatus,
  Run,
  RunMode,
  ShopPage,
  Stats,
  Watch,
  WatchEvent,
} from "./types";

declare module "axios" {
  export interface AxiosRequestConfig {
    /**
     * Responses are `{ success, data }` envelopes, and the interceptor hands
     * back the inner `data` by default. The handful of routes that carry meta
     * alongside `data` (paging, `authenticated`) set this to false and read the
     * envelope themselves.
     */
    unwrapEnvelope?: boolean;
  }
}

export const AUTH_TOKEN_KEY = "auth_token";

/**
 * localStorage throws in Safari's private mode rather than returning null, so
 * every access is guarded. A browser that will not give us a token simply makes
 * this an anonymous, read-only session.
 */
export function getStoredToken(): string | null {
  try {
    const token = window.localStorage.getItem(AUTH_TOKEN_KEY);
    return token && token.trim() ? token : null;
  } catch {
    return null;
  }
}

export function clearStoredToken(): void {
  try {
    window.localStorage.removeItem(AUTH_TOKEN_KEY);
  } catch {
    /* nothing to clear if storage is unavailable */
  }
}

/** An error the server (or the network) produced, with the status preserved. */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

export function isUnauthorized(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 401 || err.status === 403);
}

interface Envelope<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export const client = axios.create({
  baseURL: "/api",
  timeout: 20000,
  headers: { Accept: "application/json" },
});

client.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) config.headers.set("Authorization", `Bearer ${token}`);
  return config;
});

client.interceptors.response.use(
  (response) => {
    const body = response.data as Envelope<unknown> | undefined;
    // A 2xx that still reports failure is an error, not a payload.
    if (body && typeof body === "object" && body.success === false) {
      throw new ApiError(messageFrom(body, "Request failed"), response.status, body);
    }
    if (response.config.unwrapEnvelope === false) return response;
    if (body && typeof body === "object" && "data" in body) {
      response.data = body.data;
    }
    return response;
  },
  (error: unknown) => {
    // Aborts are how useApi cancels on unmount; they must stay recognisable.
    if (axios.isCancel(error)) return Promise.reject(error);
    if (error instanceof ApiError) return Promise.reject(error);

    if (axios.isAxiosError(error)) {
      const status = error.response?.status ?? 0;
      const body = error.response?.data;
      const fallback = status
        ? `Request failed (${status})`
        : "Cannot reach the watcher. Is the backend running?";
      return Promise.reject(new ApiError(messageFrom(body, fallback), status, body));
    }
    return Promise.reject(
      new ApiError(error instanceof Error ? error.message : String(error), 0, error)
    );
  }
);

function messageFrom(body: unknown, fallback: string): string {
  if (body && typeof body === "object") {
    const record = body as Record<string, unknown>;
    if (typeof record.error === "string" && record.error) return record.error;
    if (typeof record.message === "string" && record.message) return record.message;
  }
  return fallback;
}

function signalConfig(signal?: AbortSignal): AxiosRequestConfig {
  return signal ? { signal } : {};
}

/** GET with the envelope already unwrapped by the interceptor. */
async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await client.get<T>(url, config);
  return response.data;
}

/** GET keeping the envelope, for routes whose meta lives beside `data`. */
async function getEnvelope<T extends object>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await client.get<T>(url, { ...config, unwrapEnvelope: false });
  return response.data;
}

/* ------------------------------------------------------------------ *
 * Endpoints
 * ------------------------------------------------------------------ */

export function getHealth(signal?: AbortSignal): Promise<Health> {
  // /health answers with bare fields rather than an envelope.
  return getEnvelope<Health>("/health", signalConfig(signal));
}

/** The merchandised view: pages in nav order, products in page order. */
export async function getShop(signal?: AbortSignal): Promise<ShopPage[]> {
  const data = await get<{ pages: ShopPage[] }>("/shop", signalConfig(signal));
  return data?.pages ?? [];
}

export function getProducts(
  params?: { status?: ProductStatus; available?: boolean },
  signal?: AbortSignal
): Promise<Product[]> {
  return get<Product[]>("/products", { ...signalConfig(signal), params });
}

export function getProduct(productId: number, signal?: AbortSignal): Promise<ProductDetail> {
  return get<ProductDetail>(`/products/${productId}`, signalConfig(signal));
}

export function getPages(signal?: AbortSignal): Promise<Page[]> {
  return get<Page[]>("/pages", signalConfig(signal));
}

export interface EventQuery {
  type?: EventType;
  severity?: EventSeverity;
  productId?: number;
  limit?: number;
  skip?: number;
}

export async function getEvents(params?: EventQuery, signal?: AbortSignal): Promise<EventPage> {
  const body = await getEnvelope<Envelope<WatchEvent[]> & { total?: number; limit?: number; skip?: number }>(
    "/events",
    { ...signalConfig(signal), params }
  );
  const events = body.data ?? [];
  return {
    events,
    total: body.total ?? events.length,
    limit: body.limit ?? params?.limit ?? events.length,
    skip: body.skip ?? params?.skip ?? 0,
  };
}

export function getRuns(limit = 20, signal?: AbortSignal): Promise<Run[]> {
  return get<Run[]>("/runs", { ...signalConfig(signal), params: { limit } });
}

export function getStats(signal?: AbortSignal): Promise<Stats> {
  return get<Stats>("/stats", signalConfig(signal));
}

export interface MeResponse {
  user: AuthUser | null;
  authenticated: boolean;
}

export async function getMe(signal?: AbortSignal): Promise<MeResponse> {
  const body = await getEnvelope<Envelope<never> & { user?: AuthUser; authenticated?: boolean }>(
    "/me",
    signalConfig(signal)
  );
  const user = body.user ?? null;
  return { user, authenticated: body.authenticated ?? Boolean(user) };
}

export function getWatches(signal?: AbortSignal): Promise<Watch[]> {
  return get<Watch[]>("/watches", signalConfig(signal));
}

export async function createWatch(
  input: { productId: number; sizes?: string[] },
  signal?: AbortSignal
): Promise<Watch> {
  const response = await client.post<Watch>(
    "/watches",
    { productId: input.productId, sizes: input.sizes ?? [] },
    signalConfig(signal)
  );
  return response.data;
}

export async function deleteWatch(watchId: string, signal?: AbortSignal): Promise<void> {
  await client.delete(`/watches/${watchId}`, signalConfig(signal));
}

/**
 * What POST /api/admin/poll answers with. A poll walks the whole site, so the
 * route queues the run and answers 202 immediately rather than holding the
 * request open — which means the response carries no Run.
 */
export interface PollAccepted {
  mode: RunMode;
  started: boolean;
}

/**
 * Asks the watcher to poll now.
 *
 * The Run this creates arrives on the "ap:run-started" socket event; there is
 * nothing about it in this response. Callers that want to show the run in
 * flight should listen for that event (and refetch /runs as a fallback for a
 * client with no socket), never build a Run out of what comes back here.
 */
export async function triggerPoll(
  mode: RunMode = "manual",
  signal?: AbortSignal
): Promise<PollAccepted> {
  const response = await client.post<PollAccepted>("/admin/poll", { mode }, signalConfig(signal));
  return response.data;
}

/* ------------------------------------------------------------------ *
 * useApi
 * ------------------------------------------------------------------ */

export interface UseApiResult<T> {
  data: T | null;
  loading: boolean;
  /** A human-readable message, or null. Safe to render directly. */
  error: string | null;
  reload: () => void;
}

interface ApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

/**
 * Runs `fn` on mount and whenever `deps` change, and again on `reload()`.
 *
 * `fn` receives an AbortSignal that fires when the component unmounts or the
 * deps change, so in-flight requests are cancelled and no state is set
 * afterwards. Pass it straight to any of the endpoint functions above.
 *
 *   const { data, loading, error, reload } = useApi((s) => getShop(s), []);
 */
export function useApi<T>(
  fn: (signal: AbortSignal) => Promise<T>,
  deps: DependencyList = []
): UseApiResult<T> {
  const [state, setState] = useState<ApiState<T>>({ data: null, loading: true, error: null });
  const [tick, setTick] = useState(0);

  // Held in a ref so an inline arrow function does not retrigger the fetch;
  // `deps` alone decides when the request is repeated.
  const fnRef = useRef(fn);
  fnRef.current = fn;

  // Distinguishes a reload (keep showing the old data) from a deps change
  // (the old data belongs to a different query and must be dropped).
  const tickRef = useRef(tick);

  const reload = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    const isReload = tickRef.current !== tick;
    tickRef.current = tick;
    let active = true;

    setState((prev) => ({ data: isReload ? prev.data : null, loading: true, error: null }));

    fnRef
      .current(controller.signal)
      .then((result) => {
        if (active) setState({ data: result, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (!active || controller.signal.aborted || axios.isCancel(err)) return;
        setState((prev) => ({ data: prev.data, loading: false, error: toMessage(err) }));
      });

    return () => {
      active = false;
      controller.abort();
    };
    // `fn` is intentionally excluded: it is read through fnRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  return { data: state.data, loading: state.loading, error: state.error, reload };
}

export function toMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  if (typeof err === "string" && err) return err;
  return "Something went wrong.";
}

/* ------------------------------------------------------------------ *
 * Announcements
 * ------------------------------------------------------------------ */

/** Public: the live notice for the landing page, or null when there is none. */
export function getAnnouncement(signal?: AbortSignal): Promise<Announcement | null> {
  return get<Announcement | null>("/announcement", signalConfig(signal));
}

export function getAnnouncements(signal?: AbortSignal): Promise<AnnouncementRecord[]> {
  return get<AnnouncementRecord[]>("/admin/announcements", signalConfig(signal));
}

export async function createAnnouncement(
  input: { message: string; expiresAt?: string },
  signal?: AbortSignal
): Promise<AnnouncementRecord> {
  const response = await client.post<AnnouncementRecord>(
    "/admin/announcements",
    input,
    signalConfig(signal)
  );
  return response.data;
}

export async function retireAnnouncement(id: string, signal?: AbortSignal): Promise<void> {
  await client.delete(`/admin/announcements/${id}`, signalConfig(signal));
}
